import { createSelector } from 'redux-starter-kit';
import { unFormatPercent } from 'utils';

const formSelector = (state: any) => state.form;
const riskAssetAllocationSelector = (state: any) =>
  state.meta.riskAssetAllocations;
const metaSelector = (state: any) => state.meta;
const forecastSelector = (state: any) => state.meta.returnForecasts;
const riskCapacitySelector = (state: any) => state.meta.riskCapacity;
const riskRequirementLevelsSelector = (state: any) =>
  state.meta.riskRequirementLevels;

export const metaSelectors = {
  isMetaLoaded: createSelector(metaSelector, subState => subState.isMetaLoaded),
  isLoadingMeta: createSelector(
    metaSelector,
    subState => subState.isLoadingMeta
  ),
  contributionSources: createSelector(
    metaSelector,
    subState => subState.contributionSources
  ),
  contributionFrequencies: createSelector(
    metaSelector,
    subState => subState.contributionFrequencies
  ),
  contributionAccounts: createSelector(
    metaSelector,
    subState => subState.contributionAccounts
  ),
  policyProviders: createSelector(
    metaSelector,
    subState => subState.policyProviders
  ),
  policyTypes: createSelector(metaSelector, subState => subState.policyTypes),
  policyPlatforms: createSelector(
    metaSelector,
    subState => subState.policyPlatforms
  ),
  knowledgeAndExperience: createSelector(
    metaSelector,
    subState => subState.knowledgeLevels
  ),
  riskLevels: createSelector(metaSelector, subState => subState.riskLevels),
  chargeTypes: createSelector(
    metaSelector,
    subState => subState.policyChargeTypes
  ),
  assetTypes: createSelector(metaSelector, subState => subState.assetClasses),
  policyActionTypes: createSelector(
    metaSelector,
    subState => subState.policyActionTypes
  ),
  advisorChargeTypes: createSelector(
    metaSelector,
    subState => subState.advisorChargeTypes
  ),
  advisorChargeAccounts: createSelector(
    metaSelector,
    subState => subState.advisorChargeAccounts
  ),
  riskAllocations: createSelector(
    formSelector,
    riskAssetAllocationSelector,
    (subState, riskAssetAllocationSelector) => {
      if (!subState.riskProfile) return false;
      const valuesExist =
        !!subState.riskProfile.values.time_horizon_interval &&
        !!subState.riskProfile.values.recommended_risk_profile;

      if (valuesExist) {
        const { time_horizon_interval, recommended_risk_profile } =
          subState.riskProfile.values;

        const filteredData =
          riskAssetAllocationSelector.filter((risk: any) => {
            const riskLevel = Number(recommended_risk_profile);

            return (
              risk.risk_level === riskLevel &&
              risk.time_horizon === Number(time_horizon_interval)
            );
          }) || [];

        return filteredData[0] ? filteredData[0].allocations : [];
      }
      return false;
    }
  ),
  forecasts: createSelector(
    formSelector,
    forecastSelector,
    (subState, forecastSelector) => {
      if (!subState.riskProfile) return false;
      const valuesExist =
        !!subState.riskProfile.values.time_horizon_interval &&
        !!subState.riskProfile.values.recommended_risk_profile;

      if (valuesExist) {
        const { time_horizon_interval, recommended_risk_profile } =
          subState.riskProfile.values;

        const filteredData = forecastSelector.filter((forecast: any) => {
          const riskLevel = Number(recommended_risk_profile);
          return (
            forecast.risk_level === riskLevel &&
            forecast.time_horizon === Number(time_horizon_interval)
          );
        });

        return filteredData[0] ? filteredData[0].forecasts : [];
      }
      return false;
    }
  ),
  forecastTypes: createSelector(
    metaSelector,
    subState => subState.returnForecastTypes
  ),
  riskCapacityLevel: createSelector(
    formSelector,
    riskCapacitySelector,
    (form, riskCapacityLevels) => {
      if (!form.riskProfile) return false;
      const valueExist = !!form.riskProfile.values.risk_capacity;
      if (valueExist) {
        const { risk_capacity } = form.riskProfile.values;

        const riskFormatted = Number(
          Number(unFormatPercent(risk_capacity)).toFixed(2)
        );

        const filteredData = riskCapacityLevels.filter((level: any) => {
          const maxValue = -level.min * 100;
          const minValue = -level.max * 100;

          return riskFormatted >= minValue && riskFormatted <= maxValue;
        });

        return filteredData[0] ? filteredData[0] : [];
      }
      return false;
    }
  ),
  riskRequirementLevel: createSelector(
    formSelector,
    riskRequirementLevelsSelector,
    (form, riskRequirementLevels) => {
      if (!form.riskProfile || riskRequirementLevels.length === 0) return false;
      const valueExist =
        !!form.riskProfile.values.required_return &&
        !!form.riskProfile.values.time_horizon_interval;
      if (valueExist) {
        const { required_return, time_horizon_interval } =
          form.riskProfile.values;

        // risk

        const filteredData = riskRequirementLevels[
          time_horizon_interval
        ].filter((level: any) => {
          const maxValue = (level.max * 100).toFixed(4);
          const minValue = (level.min * 100).toFixed(4);
          const unFormatedRequired = Number(unFormatPercent(required_return));
          const roundedReturn = Number(unFormatedRequired.toFixed(4));

          return (
            roundedReturn >= Number(minValue) &&
            roundedReturn <= Number(maxValue)
          );
        });

        return filteredData[0];
      }
      return false;
    }
  ),
  assetClasses: createSelector(
    metaSelector,
    subState => subState.assetClassesNew
  ),
  riskDescriptions: createSelector(
    metaSelector,
    subState => subState.riskDescriptions
  ),
  cipThresHolds: createSelector(
    metaSelector,
    subState => subState.cipThresHolds
  ),
  modelPortfolios: createSelector(
    metaSelector,
    subState => subState.modelPortfolios
  ),
  policyTypesRecommended: createSelector(
    metaSelector,
    subState => subState.policyTypesRecommended
  ),
  timeHorizons: createSelector(metaSelector, subState => subState.timeHorizons),
  chargeSources: createSelector(
    metaSelector,
    subState => subState.chargeSources
  ),
  benchmarks: createSelector(metaSelector, subState => subState.benchmarks),
  cashFund: createSelector(metaSelector, subState => subState.cashFund),
  benchmarkConfig: createSelector(
    metaSelector,
    subState => subState.benchmarkConfig
  )
};
